/*!

=========================================================
* Argon Dashboard React - v1.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

// reactstrap components
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Card, CardBody, CardHeader, Col, Collapse, Container, Row } from "reactstrap";
import Toastr from "toastr";
import Typewriter from "typewriter-effect";
import { useQueryClient } from "react-query";
import ExecutionInfoHeader from '../components/Headers/ExecutionInfoHeader';
import { useGetExecutionInfo } from "../hooks/useGetExecutionInfo";
import { ExecutionInputType } from "../types";
import SingleQuestion from '../components/SingleQuestion';
import ExtractedText from "../components/ExtractedText";
import { useAskQuestion } from "../hooks/useAskQuestion";
// import { copyTextToClipboard } from "../utils/copyToClipboard";
import { useGetQuestions } from "../hooks/useGetQuestions";
import WordCloudSection from "../components/WordCloudSection";
// import { useParaphraseQuestion } from "../hooks/useParaphraseQuestion";
import KeyphraseSection from "../components/KeyphraseSection";
import {
  NavigateNext, NavigateBefore, MenuBook,
  DynamicFeed, Equalizer, ArrowBack, ArrowForward
} from "@material-ui/icons";
import { useCurrentUser } from '../hooks/useCurrentUser';
import { UserPermissions } from '../types';

const ExecutionInfo: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const queryClient = useQueryClient();
  const [view] = useState<"text" | "redacted">("text");
  const [searchTerm, setSearchTerm] = useState("");
  const [finalQuestion, setFinalQuestion] = useState("");
  const [questionText, setQuestionText] = useState("");
  const [finalSearchTerm, setFinalSearchTerm] = useState("");
  const [activeExtractedTextIdx, setActiveExtractedTextIdx] = useState(0);
  const [txtCollapsed, setTxtCollapsed] = useState(false);
  const [gptQna, setGptQna] = useState(false);
  const [callFromOpenLLM, setCallFromOpenLLM] = useState<boolean>(false)
  const [analysisCollapsed] = useState(false)
  const [currentDocumentPage, setCurrentDocumentPage] = useState(0);

  const { data: currentUserData } = useCurrentUser();

  const { data: executionInfo, isLoading, isError } = useGetExecutionInfo(id, {
    enabled: true,
    refetchInterval: false
  })

  const nextDocumentPage = () => {
    let nextPage = currentDocumentPage + 1;

    setCurrentDocumentPage(nextPage);
  }

  const previousDocumentPage = () => {
    if (currentDocumentPage > 0) {
      let nextPage = currentDocumentPage - 1;

      setCurrentDocumentPage(nextPage);
    }
  }

  // const {
  //   data: paraphrasedQuestions,
  //   refetch: paraphraseQuestion,
  // } = useParaphraseQuestion(finalQuestion, executionInfo?.id || 0)

  const {
    refetch: askQuestion,
    data: askQuestionResponse,
    isFetching: askQuestionLoading,
    isError: askQuestionError,
  } = useAskQuestion(finalQuestion, executionInfo?.id || 0, gptQna, callFromOpenLLM);

  const { data: questions, refetch: getQuestions } = useGetQuestions(executionInfo?.id || 0, {
    enabled: typeof executionInfo?.id === "number",
  })

  useEffect(() => {
    if (!askQuestionResponse) return
    queryClient.removeQueries(["execution", askQuestionResponse?.id || 0, "questions"]);
    getQuestions()
    // paraphraseQuestion()
    document.querySelector("#asked_question_section")?.scrollIntoView({
      behavior: "smooth",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askQuestionResponse])

  useEffect(() => {
    if (askQuestionError) {
      Toastr.error("Something went wrong. Please try again later");
    }
  }, [askQuestionError])

  useEffect(() => {
    if (finalQuestion.trim().length > 0) {
      askQuestion();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalQuestion, gptQna, callFromOpenLLM]);

  const pastQuestions = useMemo(() => {
    if (!questions) return []
    return questions
  }, [questions])

  // const sources = useMemo(() => {
  //   if (askQuestionResponse?.questions === undefined || askQuestionResponse?.questions.length === 0) return []
  //   if (askQuestionResponse?.questions[0].qnaResponse?.sources === undefined) return []
  //   return askQuestionResponse.questions[0].qnaResponse.sources
  // }, [askQuestionResponse])

  // const wordsSources = useMemo(() => {
  //   return (sources.length === 0 ? [[{ value: '' }]] : sources).flat().map(c => c.value).reduce((acc, curr) => acc + curr).split(' ').filter(w => w.length > 0).length
  // }, [sources])

  // const wordsOutput = useMemo(() => {
  //   if (askQuestionResponse?.questions === undefined || askQuestionResponse?.questions.length === 0) return 0
  //   try {
  //     return (Array.isArray(askQuestionResponse.questions[0]!.qnaResponse?.responses) ? askQuestionResponse.questions[0]!.qnaResponse!.responses : []).reduce((acc, curr) => acc + curr).split(' ').filter(w => w.length > 0).length
  //   } catch (error) {
  //     return 0
  //   }
  // }, [askQuestionResponse])

  const handleAskQuestion = (gq: boolean, openllm: boolean) => {
    if (questionText.trim().length === 0) {
      alert("Please enter a question");
      return;
    }
    if (questionText.trim().length > 4000) {
      alert('Question should be at most 4000 characters');
      return;
    }
    if (!executionInfo?.numWords || !executionInfo?.numSentences) {
      alert('No text to ask question from!')
      return;
    }
    setSearchTerm("");
    setFinalSearchTerm("");
    setTxtCollapsed(false);
    setGptQna(gq)
    setCallFromOpenLLM(openllm);
    setFinalQuestion(questionText);
  };

  // const handleCopyAllSources = () => {
  //   if (askQuestionResponse?.questions === undefined ||
  //     !askQuestionResponse.questions[0] || !askQuestionResponse.questions[0].qnaResponse) return

  //   const sources = askQuestionResponse.questions[0].qnaResponse.sources[activeSource]
  //   copyTextToClipboard(sources.map((source) => source.value).join("\n"))
  //   Toastr.success("Copied to clipboard")
  // }

  // const handleCopyAllOutput = () => {
  //   if (askQuestionResponse?.questions === undefined ||
  //     !askQuestionResponse.questions[0] || !askQuestionResponse.questions[0].qnaResponse) return

  //   const responses = askQuestionResponse.questions[0].qnaResponse.responses
  //   copyTextToClipboard(responses.join("\n"))
  //   Toastr.success("Copied to clipboard")
  // }

  return (
    <>
      <ExecutionInfoHeader
        executionInfo={executionInfo}
        isLoading={isLoading}
        error={isError}
        questions={pastQuestions}
      />

      <Container className="mt--8" fluid>
        <Card
          style={{
            backgroundColor: 'transparent',
            padding: '0',
            margin: '0',
            textAlign: 'center',
          }}
        >
          <CardBody
            style={{ background: 'transparent', padding: '0', margin: '0' }}
          >
            <Row>
              <Col className="col-12">
                <div className="mb-0 form-group">
                  <div className="input-group-alternative input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text text-warning">
                        <i className="fas fa-search"></i>
                      </span>
                    </div>
                    <input
                      value={questionText}
                      onChange={(e) => setQuestionText(e.target.value)}
                      placeholder="Ask your question from extracted text"
                      type="text"
                      className="form-control"
                    />
                  </div>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Container>

      <Container id="ask_question_btn_section" fluid>
        <Card
          style={{
            padding: '0',
            background: 'transparent',
            border: 'none',
            color: '#fff',
          }}
        >
          <CardBody>
            {!askQuestionLoading && (
              <Row>
                <Col className="text-center">
                  <Button
                    onClick={() => {
                      handleAskQuestion(false, false);
                    }}
                    disabled={askQuestionLoading}
                    className="btn-sm btn-warning"
                  >
                    {askQuestionLoading ? 'Loading...' : 'Ask Question'}
                  </Button>

                  <Button
                    onClick={() => {
                      handleAskQuestion(true, false);
                    }}
                    disabled={askQuestionLoading}
                    className="btn-sm btn-warning"
                  >
                    {askQuestionLoading ? 'Loading...' : 'Ask ChatGPT'}
                  </Button>

                  {currentUserData &&
                    currentUserData.roles.includes(
                      UserPermissions.internalLLM
                    ) && (
                      <Button
                        onClick={() => {
                          handleAskQuestion(false, true);
                        }}
                        disabled={askQuestionLoading}
                        className="btn-sm btn-warning"
                      >
                        {askQuestionLoading ? 'Loading...' : 'Ask Internal LLM'}
                      </Button>
                    )}
                </Col>
                {/* <Col className="text-left">
                
              </Col> */}
              </Row>
            )}
            {askQuestionLoading && (
              <Row>
                <Col className="text-center">
                  <Typewriter
                    options={{
                      loop: true,
                    }}
                    onInit={(typewriter) => {
                      typewriter
                        .typeString('Please wait...')
                        .pauseFor(1000)
                        .deleteAll()
                        .typeString('Thinking intensifies...')
                        .pauseFor(1000)
                        .deleteAll()
                        .typeString('Still thinking...')
                        .pauseFor(1000)
                        .start();
                    }}
                  />
                </Col>
              </Row>
            )}
          </CardBody>
        </Card>
      </Container>

      <Container id="asked_question_section" fluid>
        <Row className="mt-5">
          <Col>
            <h1>
              <DynamicFeed /> Asked Questions{' '}
              <small style={{ fontSize: '12px', letterSpacing: '.08em' }}>
                (Recent First)
              </small>
            </h1>
          </Col>
        </Row>
      </Container>
      {/* <Container className="mt--7" fluid>
        <ExecutionInfoForm
          executionInfo={executionInfo}
          questions={pastQuestions}
        />
      </Container> */}
      {/* <div className="mt-3"></div> */}

      <div className="mt-4" />
      {pastQuestions.length > 0 &&
        executionInfo &&
        pastQuestions.map((q, i) => (
          <SingleQuestion
            question={q}
            key={`${i}. ${q.question}`}
            setActiveExtractedTextIdx={setActiveExtractedTextIdx}
            setCurrentDocumentPage={setCurrentDocumentPage}
            setSearchTerm={setSearchTerm}
            setFinalSearchTerm={setFinalSearchTerm}
            setTxtCollapsed={setTxtCollapsed}
            mapping={q.redactionMapping}
            defaultOpen={i === 0}
            inputType={executionInfo.inputType}
          />
        ))}

      {pastQuestions.length < 1 && (
        <Container fluid>
          <Row>
            <Col>
              <span>No Question asked yet.</span>
            </Col>
          </Row>
        </Container>
      )}

      <Container fluid>
        <Row className="mt-5">
          <Col>
            <h1>
              <MenuBook /> {view === 'text' ? 'Extracted' : 'Redacted'} Text{' '}
            </h1>
          </Col>
        </Row>
      </Container>
      {executionInfo?.inputs && executionInfo?.inputs.length && (
        <Container fluid>
          <Card className="bg-secondary shadow extract-data-card">
            <CardHeader>
              <div className="sticky-section"></div>
              <Row className="align-items-center" id="txt_section">
                <Col>
                  <div>
                    <small>
                      <b>
                        Total:&nbsp;{executionInfo.numWords || 0}
                        &nbsp;words,&nbsp;
                        {executionInfo.numSentences || 0}&nbsp;sentences
                      </b>
                    </small>
                  </div>
                  <div style={{ display: 'inline' }}>
                    <small>
                      ({activeExtractedTextIdx + 1} of&nbsp;
                      {executionInfo.inputs.length})
                    </small>
                  </div>
                </Col>

                <Col>
                  {/* TODO: have token appended in react query hook (data can be transformed using `select` option) */}
                  <span className="float-right">
                    {(executionInfo.inputType === ExecutionInputType.file ||
                      executionInfo.inputType === ExecutionInputType.url) &&
                      executionInfo.inputs &&
                      executionInfo.inputs[activeExtractedTextIdx] && (
                        <a
                          href={`${
                            executionInfo.inputs[activeExtractedTextIdx]
                              .inputText
                          }?token=${localStorage.getItem('token')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ marginRight: '10px' }}
                        >
                          <i className="fas fa-link mr-1" />
                        </a>
                      )}
                    {/* {executionInfo.isConfidential && (
                      <Button
                        size="sm"
                        color="primary"
                        onClick={() => {
                          setView(view === "text" ? "redacted" : "text");
                        }}
                      >
                        {view === "text" ? "Redact" : "Show Text"}
                      </Button>
                    )} */}
                    {executionInfo.inputs.length > 1 && (
                      <Button
                        size="sm"
                        color="primary"
                        disabled={activeExtractedTextIdx === 0}
                        onClick={() => {
                          setCurrentDocumentPage(0);
                          if (activeExtractedTextIdx > 0) {
                            setActiveExtractedTextIdx(
                              activeExtractedTextIdx - 1
                            );
                          }
                        }}
                      >
                        <NavigateBefore />
                      </Button>
                    )}
                    {executionInfo.inputs.length > 1 && (
                      <Button
                        size="sm"
                        disabled={
                          activeExtractedTextIdx ===
                          executionInfo.inputs!.length - 1
                        }
                        color="primary"
                        onClick={() => {
                          if (
                            activeExtractedTextIdx <
                            executionInfo.inputs!.length - 1
                          ) {
                            setActiveExtractedTextIdx(
                              activeExtractedTextIdx + 1
                            );
                          }

                          setCurrentDocumentPage(0);
                        }}
                      >
                        <NavigateNext />
                      </Button>
                    )}
                    {/* <Button
                      size="sm"
                      color="primary"
                      onClick={() => {
                        setTxtCollapsed(!txtCollapsed);
                      }}
                    >
                      {txtCollapsed ? <ExpandMore/> : <ExpandLess/>}
                    </Button> */}
                  </span>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              <Collapse isOpen={!txtCollapsed}>
                <Row>
                  <Col>
                    <Col>
                      <div className="input-group-alternative input-group">
                        <div
                          className="input-group-prepend"
                          style={{ width: '100%' }}
                        >
                          <span onClick={() => {}} className="input-group-text">
                            <i className="fas fa-search"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Search for anything in extracted text"
                            id="subt_search"
                            value={searchTerm}
                            onChange={(e) => {
                              setSearchTerm(e.target.value);
                            }}
                            onKeyDown={(e) => {
                              if (e.key.toLowerCase() === 'enter') {
                                setFinalSearchTerm(searchTerm);
                              }
                            }}
                          />
                        </div>
                      </div>
                    </Col>
                  </Col>
                </Row>
                {Object.keys(
                  executionInfo.inputs[activeExtractedTextIdx]?.extractedText
                    ?.text
                ).length > 1 && (
                  <Row className="mt-2">
                    <Col>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          height: '100%',
                          float: 'right',
                        }}
                      >
                        <span style={{ float: 'left' }}>
                          Page: {currentDocumentPage + 1} /{' '}
                          {
                            Object.keys(
                              executionInfo.inputs[activeExtractedTextIdx]
                                ?.extractedText?.text
                            ).length
                          }
                        </span>
                        &nbsp;
                        <Button
                          size="sm"
                          className="btn-primary"
                          disabled={currentDocumentPage < 1}
                          onClick={() => previousDocumentPage()}
                        >
                          <ArrowBack />
                        </Button>
                        {
                          <Button
                            size="sm"
                            className="btn-primary"
                            disabled={
                              Object.keys(
                                executionInfo.inputs[activeExtractedTextIdx]
                                  ?.extractedText?.text
                              ).length <=
                              currentDocumentPage + 1
                            }
                            onClick={() => nextDocumentPage()}
                          >
                            <ArrowForward />
                          </Button>
                        }
                      </div>
                    </Col>
                  </Row>
                )}
                <ExtractedText
                  text={
                    executionInfo.inputs[activeExtractedTextIdx]?.extractedText
                      ?.text[currentDocumentPage]
                  }
                  redactedHTML={
                    executionInfo.redactedHTML
                      ? executionInfo.redactedHTML[activeExtractedTextIdx]
                      : undefined
                  }
                  view={view}
                  searchTerm={finalSearchTerm}
                />
              </Collapse>
            </CardBody>
          </Card>
        </Container>
      )}
      <div className="mt-4" />
      <Container fluid>
        <Row className="mt-5">
          <Col>
            <h1>
              <Equalizer /> Analysis{' '}
            </h1>
          </Col>
        </Row>
      </Container>

      <div className="mt-4" />
      {executionInfo && (
        <Container fluid className="mb-4">
          <Card className="shadow">
            <Collapse isOpen={!analysisCollapsed}>
              {(executionInfo.wc2Words || executionInfo.wc3Words) && (
                <WordCloudSection
                  analyseResponse={executionInfo}
                  handleKeyPhrase={(word) => {
                    setSearchTerm(word);
                    setFinalSearchTerm(word);
                    document.querySelector('#txt_section')?.scrollIntoView({
                      behavior: 'smooth',
                    });
                    setTxtCollapsed(false);
                  }}
                />
              )}
            </Collapse>
          </Card>
        </Container>
      )}
      <div className="mt-4" />
      {executionInfo && (
        <Container fluid className="mb-4">
          <Card className="shadow">
            <Collapse isOpen={!analysisCollapsed}>
              <CardBody
                style={{
                  backgroundColor: '#fff',
                  borderRadius: '5px',
                }}
              >
                {executionInfo.keyphrases && (
                  <KeyphraseSection
                    analyseResponse={executionInfo}
                    handleKeyPhrase={(word) => {
                      setSearchTerm(word);
                      setFinalSearchTerm(word);
                      document.querySelector('#txt_section')?.scrollIntoView({
                        behavior: 'smooth',
                      });
                      setTxtCollapsed(false);
                    }}
                  />
                )}
              </CardBody>
            </Collapse>
          </Card>
        </Container>
      )}
    </>
  );
};

export default ExecutionInfo;
